// FILE: src/features/supportSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWithAuth from "../utils/fetchWithAuth";

// createTicket => POST /api/support
export const createTicket = createAsyncThunk(
  "support/createTicket",
  async ({ category, description }, { rejectWithValue }) => {
    try {
      // POST /api/support
      const response = await fetchWithAuth("/api/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category, description })
      });
      // Suppose the response is { _id, category, description, status, createdAt, ... }
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to create support ticket");
    }
  }
);

// fetchMyTickets => GET /api/support/my (if the backend has it)
export const fetchMyTickets = createAsyncThunk(
  "support/fetchMyTickets",
  async ({ status, category, sort, order, page = 1, limit = 50 } = {}, { rejectWithValue }) => {
    try {
      // The server pages (default 10); ask for a full page and keep the total.
      const params = [`page=${page}`, `limit=${limit}`];
      if (status && status !== "all") {
        params.push(`status=${encodeURIComponent(status)}`);
      }
      if (category && category !== "all") {
        params.push(`category=${encodeURIComponent(category)}`);
      }
      if (sort) {
        params.push(`sort=${encodeURIComponent(sort)}`);
      }
      if (order) {
        params.push(`order=${encodeURIComponent(order)}`);
      }
      let url = "/api/support/my";
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }

      const response = await fetchWithAuth(url);
      // Suppose this returns an array of tickets, e.g. [{_id, category, status, createdAt, ...}, ...]
      return response; // the array
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch user tickets");
    }
  }
);

// fetchTicketDetail => GET /api/support/:id
export const fetchTicketDetail = createAsyncThunk(
  "support/fetchTicketDetail",
  async (ticketId, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(`/api/support/${ticketId}`);
      // Suppose it returns { _id, category, status, createdAt, messages: [ { sender, message, createdAt }, ... ] }
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch ticket detail");
    }
  }
);

// postTicketMessage => POST /api/support/:id/messages
export const postTicketMessage = createAsyncThunk(
  "support/postTicketMessage",
  async ({ ticketId, message }, { rejectWithValue }) => {
    try {
      const response = await fetchWithAuth(
        `/api/support/${ticketId}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message })
        }
      );
      // The server answers { message, ticket } with the updated ticket.
      return { ticketId, ticket: response?.ticket || null };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to post message");
    }
  }
);

const supportSlice = createSlice({
  name: "support",
  initialState: {
    tickets: [], // array of the student's tickets
    loading: false,
    currentTicket: null, // for detail view
    error: null,
    creating: false,
    createError: null,
    postingMessage: false,
    postMessageError: null
  },
  reducers: {},
  extraReducers: (builder) => {
    // createTicket
    builder
      .addCase(createTicket.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        state.creating = false;
        // push the new ticket into state.tickets if you want immediate UI reflection
        state.tickets.push(action.payload);
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.creating = false;
        state.createError = action.payload || "Error creating ticket";
      });

    // fetchMyTickets
    builder
      .addCase(fetchMyTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = action.payload.tickets || []; // the array of user’s tickets
        state.totalCount = action.payload.totalCount ?? state.tickets.length;
      })
      .addCase(fetchMyTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching user tickets";
      });
    // fetchTicketDetail
    builder
      .addCase(fetchTicketDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentTicket = null;
      })
      .addCase(fetchTicketDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTicket = action.payload; // entire ticket object
      })
      .addCase(fetchTicketDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching ticket detail";
        state.currentTicket = null;
      });

    // postTicketMessage
    builder
      .addCase(postTicketMessage.pending, (state) => {
        state.postingMessage = true;
        state.postMessageError = null;
      })
      .addCase(postTicketMessage.fulfilled, (state, action) => {
        state.postingMessage = false;
        const { ticketId, ticket } = action.payload;
        // Take the server's ticket (messages, status, updatedAt); keep the
        // populated owner the detail request returned.
        if (ticket && state.currentTicket && state.currentTicket._id === ticketId) {
          state.currentTicket = { ...state.currentTicket, ...ticket, userId: state.currentTicket.userId };
        }
      })
      .addCase(postTicketMessage.rejected, (state, action) => {
        state.postingMessage = false;
        state.postMessageError = action.payload || "Error posting new message";
      });
  }
});

export default supportSlice.reducer;
